import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import TrustBadges from "@/components/TrustBadges";
import BestsellersCarousel from "@/components/BestsellersCarousel";
import { tFamily } from "@/i18n";
import { ArrowRight } from "lucide-react";

const familyImages = {
  citrico: "https://images.unsplash.com/photo-1610461888750-10bfc601b874?auto=format&fit=crop&q=85&w=800",
  amaderado: "https://images.unsplash.com/photo-1615634260167-c8cdede054de?auto=format&fit=crop&q=85&w=800",
  floral: "https://images.unsplash.com/photo-1523293182086-7651a899d37f?auto=format&fit=crop&q=85&w=800",
  oriental: "https://images.unsplash.com/photo-1541643600914-78b084683601?auto=format&fit=crop&q=85&w=800",
  gourmand: "https://images.unsplash.com/photo-1594035910387-fea47794261f?auto=format&fit=crop&q=85&w=800",
};

export default function Home() {
  const { t, i18n } = useTranslation();
  const [products, setProducts] = useState([]);
  useEffect(() => { api.get("/products").then(({ data }) => setProducts(data)); }, []);

  const news = products.filter(p => p.badges?.includes("Nuevo")).slice(0, 4);
  const limited = products.filter(p => p.badges?.includes("Edición limitada")).slice(0, 4);

  const familyCopy = i18n.language === "en" ? {
    citrico: "Mediterranean light and freshness.",
    amaderado: "Warm, elegant, timeless.",
    floral: "The classic bouquet, made modern.",
    oriental: "Amber, oud, spice — magnetic sillage.",
    gourmand: "Vanilla, coffee, edible notes.",
  } : {
    citrico: "Luz y frescura mediterránea.",
    amaderado: "Cálidos, elegantes, atemporales.",
    floral: "El bouquet clásico contemporáneo.",
    oriental: "Ámbar, oud, especias — huella magnética.",
    gourmand: "Vainilla, café, notas comestibles.",
  };

  const families = ["citrico", "amaderado", "floral", "oriental", "gourmand"].map((key) => ({
    key, label: tFamily(key), img: familyImages[key], copy: familyCopy[key],
  }));

  return (
    <div>
      {/* Hero */}
      <section className="relative min-h-[85vh] flex items-end bg-navy text-cream overflow-hidden" data-testid="home-hero">
        <img
          src="https://images.unsplash.com/photo-1705899844877-81bb0a0665c1?auto=format&fit=crop&q=85&w=2000"
          alt="Editorial luxury perfume"
          className="absolute inset-0 w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/70 to-navy/30" />
        <div className="relative max-w-[1600px] mx-auto w-full px-6 lg:px-12 pb-16 md:pb-24 grid md:grid-cols-12 gap-8 items-end">
          <div className="md:col-span-8 fade-up">
            <div className="flex items-center gap-4 mb-6">
              <img src="/brand/logo.png" alt="" className="h-16 w-16 object-cover rounded-full" />
              <div className="font-mono-caps text-cream/80">
                {i18n.language === "en" ? "New collection · Winter 2026" : "Nueva colección · Invierno 2026"}
              </div>
            </div>
            <h1 className="font-serif text-5xl sm:text-7xl md:text-8xl leading-[0.95] tracking-tight text-cream">
              {i18n.language === "en" ? (
                <>Perfumes<br /><span className="italic text-cream">100% authentic.</span></>
              ) : (
                <>Perfumes<br /><span className="italic text-cream">100% originales.</span></>
              )}
            </h1>
            <p className="mt-8 text-cream/70 max-w-lg text-base leading-relaxed">
              {i18n.language === "en"
                ? "High-end fragrances sourced from official distributors. Fast shipping and expert service."
                : "Fragancias de gama alta seleccionadas con distribuidores oficiales. Envío rápido y atención experta."}
            </p>
          </div>
          <div className="md:col-span-4 fade-up flex flex-col gap-4" style={{ animationDelay: "150ms" }}>
            <Link to="/shop" className="group inline-flex items-center justify-between bg-cream text-navy px-6 py-5 font-mono-caps hover:bg-white transition-colors" data-testid="hero-cta-shop">
              {t("hero.cta_shop")} <ArrowRight strokeWidth={1.25} className="h-5 w-5 transition-transform group-hover:translate-x-1" />
            </Link>
            <Link to="/autenticidad" className="inline-flex items-center justify-between border border-cream/40 text-cream px-6 py-5 font-mono-caps hover:bg-cream hover:text-navy transition-colors" data-testid="hero-cta-auth">
              {t("nav.authenticity")} <ArrowRight strokeWidth={1.25} className="h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      <TrustBadges />

      {/* Novedades */}
      {news.length > 0 && (
        <section className="py-20 md:py-28" data-testid="home-news">
          <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
            <div className="flex items-end justify-between mb-10">
              <div>
                <div className="font-mono-caps text-muted-foreground">{t("home.news_eyebrow")}</div>
                <h2 className="font-serif text-4xl md:text-5xl mt-2">{t("home.news_title")}</h2>
              </div>
              <Link to="/shop?sort=newest" className="hidden md:inline-block editorial-link font-mono-caps">{t("finder.see_all")}</Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
              {news.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
            </div>
          </div>
        </section>
      )}

      {/* Familia olfativa */}
      <section className="py-20 md:py-28 bg-muted" data-testid="home-families">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
          <div className="max-w-2xl mb-12">
            <div className="font-mono-caps text-muted-foreground">{t("home.families_eyebrow")}</div>
            <h2 className="font-serif text-4xl md:text-5xl mt-2 leading-tight">{t("home.families_title")}</h2>
            <p className="text-muted-foreground mt-4 leading-relaxed">
              {i18n.language === "en"
                ? "Every family tells a different story. Find yours."
                : "Cada familia cuenta una historia distinta. Encuentra la tuya."}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {families.map((f, i) => (
              <Link
                key={f.key}
                to={`/shop?family=${f.key}`}
                className="group relative aspect-[3/4] md:aspect-[4/6] overflow-hidden bg-black fade-up"
                style={{ animationDelay: `${i * 80}ms` }}
                data-testid={`family-${f.key}`}
              >
                <img src={f.img} alt={f.label} className="w-full h-full object-cover opacity-70 transition-transform duration-500 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-6 text-white">
                  <div className="font-serif text-2xl">{f.label}</div>
                  <div className="text-xs text-white/70 mt-1">{f.copy}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Fragrance Finder CTA */}
      <section className="py-16 md:py-20 bg-royal text-white" data-testid="home-finder-cta">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="font-mono-caps text-white/80 mb-4">{t("home.finder_eyebrow")}</div>
            <h2 className="font-serif text-4xl md:text-6xl leading-tight">
              {t("home.finder_title_a")}<br /><span className="italic">{t("home.finder_title_b")}</span>
            </h2>
            <p className="mt-6 text-white/80 leading-relaxed max-w-lg">
              {t("home.finder_copy")}
            </p>
            <Link
              to="/finder"
              className="inline-flex items-center gap-3 mt-8 bg-cream text-navy px-6 py-4 font-mono-caps hover:bg-white transition-colors"
              data-testid="home-finder-btn"
            >
              {t("home.finder_btn")} <ArrowRight strokeWidth={1.25} className="h-4 w-4" />
            </Link>
          </div>
          <div className="aspect-[4/5] overflow-hidden hidden md:block">
            <img src="https://images.unsplash.com/photo-1541643600914-78b084683601?auto=format&fit=crop&q=85&w=1200" alt="Fragrance finder" className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      {/* Bestsellers — curated carousel */}
      <BestsellersCarousel />

      {/* Authenticity CTA */}
      <section className="py-24 md:py-32 bg-navy text-cream" data-testid="home-auth-cta">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="font-mono-caps text-royal mb-4">Garantía de autenticidad</div>
            <h2 className="font-serif text-5xl md:text-6xl leading-tight">
              Cada frasco,<br /><span className="italic text-cream">verificado.</span>
            </h2>
            <p className="mt-6 text-cream/70 leading-relaxed max-w-lg">
              Trabajamos únicamente con distribuidores oficiales. Cada lote se verifica antes de salir del almacén. Si algo no cuadra, te devolvemos tu dinero.
            </p>
            <Link to="/autenticidad" className="inline-flex items-center gap-3 mt-8 border border-cream text-cream px-6 py-4 font-mono-caps hover:bg-cream hover:text-navy transition-colors" data-testid="auth-cta-btn">
              Cómo lo garantizamos <ArrowRight strokeWidth={1.25} className="h-4 w-4" />
            </Link>
          </div>
          <div className="aspect-[4/5] overflow-hidden">
            <img src="https://images.unsplash.com/photo-1644820850758-ffd841aa235c?auto=format&fit=crop&q=85&w=1200" alt="Autenticidad" className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      {/* Limited editions */}
      {limited.length > 0 && (
        <section className="py-20 md:py-28" data-testid="home-limited">
          <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
            <div className="flex items-end justify-between mb-10">
              <div>
                <div className="font-mono-caps text-muted-foreground">04 · {i18n.language === "en" ? "Collection" : "Colección"}</div>
                <h2 className="font-serif text-4xl md:text-5xl mt-2">{t("footer.editions")}</h2>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
              {limited.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
            </div>
          </div>
        </section>
      )}

      {/* Testimonials */}
      <section className="py-20 md:py-28 border-t border-border" data-testid="home-testimonials">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="max-w-2xl mb-12">
            <div className="font-mono-caps text-muted-foreground">05 · Voces</div>
            <h2 className="font-serif text-4xl md:text-5xl mt-2">Lo que dicen nuestros clientes</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { text: "Pedido llegado en 48h y el frasco es idéntico al que tenía de la tienda oficial. Cero dudas de originalidad.", author: "María G." },
              { text: "El servicio al cliente es de otra liga. Me ayudaron a elegir un aroma acorde a mi personalidad.", author: "David L." },
              { text: "Compré una edición limitada difícil de encontrar y todo el proceso fue impecable.", author: "Nuria B." },
            ].map((t, i) => (
              <blockquote key={i} className="border-l border-royal pl-6" data-testid={`testimonial-${i}`}>
                <p className="font-serif text-xl leading-relaxed">"{t.text}"</p>
                <cite className="not-italic font-mono-caps text-xs text-muted-foreground mt-4 block">— {t.author} · Verificado</cite>
              </blockquote>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
